import { useEffect, useState, useCallback, type ReactNode } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Group,
  Box,
  Button,
  Badge,
  Progress,
  Loader,
  Center,
  ThemeIcon,
  Divider,
  Avatar,
  Modal,
  Menu,
  ActionIcon,
  Alert,
  Tooltip,
} from '@mantine/core';
import {
  Users,
  User,
  BookOpen,
  Calendar,
  ArrowRight,
  Zap,
  Clock,
  Video,
  Check,
  File,
  Link as LinkIcon,
  MessageSquare,
  MoreVertical,
  RefreshCw,
  XCircle,
  AlertTriangle,
  Mic,
} from 'react-feather';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.js';
import { useParticipantCohortStore } from '../../stores/participantCohort.js';
import { participantPortalApi } from '../../services/api/participantPortal.js';
import type {
  ParticipantDashboardData,
  ParticipantLiveSession,
  ParticipantModality,
  ParticipantUpcomingSession,
  ParticipantActionItem,
  ParticipantOneOnOneCoaching,
  ParticipantOneOnOneSession,
  ParticipantWorkshop,
  ParticipantWorkshopResource,
  ParticipantGroupCoaching,
  ParticipantGroupCoachingSession,
  ParticipantPeerLeaderCallout,
  WorkshopAttendanceStatus,
  ModalityType,
} from '../../services/api/participantPortal.js';
import { ScheduleSessionModal } from './components/ScheduleSessionModal.js';
import { ScheduleCheckinModal } from './components/ScheduleCheckinModal.js';
import { useFeatureFlag } from '../../stores/featureFlags.js';
import { DashboardRefreshView } from './dashboardRefresh/DashboardRefreshView.js';
import { CheckinLineItem } from './components/CheckinLineItem.js';
import { CheckinActionsMenu } from './components/CheckinActionsMenu.js';
import { AttendancePendingBadge } from './components/AttendancePendingBadge.js';
import { isPastAwaitingAttendance } from '../cohorts/components/sessions/sessionsHelpers';
import { canChangeBooking } from './bookingActions.js';
import type {
  ParticipantGroupCoachingCoach,
  ParticipantHuddleType,
} from '../../services/api/participantPortal.js';
import { getOneOnOneTypeConfig } from '../../config/oneOnOneTypeConfig';
import { parseDateOnly } from '../../utils/dateHelpers';
import { isCheckinOneOnOneType } from '../../types';
import {
  AWAITING_COACH_SCHEDULE_LABEL,
  AWAITING_COACH_TOOLTIP_COPY,
} from './awaitingCoachSchedule.js';
import { getZoneCode } from '../../utils/formatters';

// ============================================================================
// Helpers
// ============================================================================

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  // MED-1389: name the zone ("9:00 AM ET"), as the coach view does.
  return `${time} ${getZoneCode(null, d)}`;
}

function formatDateRange(start: string | null, end: string | null): string {
  if (!start) return '';
  // MED-1383: cohort dates are date-only; parse them as calendar dates, not UTC midnight.
  const fmt = (d: string) =>
    (parseDateOnly(d) ?? new Date(d)).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  return end ? `${fmt(start)} – ${fmt(end)}` : fmt(start);
}

function getRelativeLabel(scheduledStart: string): string {
  const sessionDate = new Date(scheduledStart);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // End of this week (Sunday)
  const endOfThisWeek = new Date(startOfToday);
  endOfThisWeek.setDate(startOfToday.getDate() + (7 - startOfToday.getDay()));
  const endOfNextWeek = new Date(endOfThisWeek);
  endOfNextWeek.setDate(endOfThisWeek.getDate() + 7);

  if (sessionDate < endOfThisWeek) return 'THIS WEEK';
  if (sessionDate < endOfNextWeek) return 'NEXT WEEK';
  return sessionDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getCountdownMinutes(scheduledStart: string): number {
  const diff = new Date(scheduledStart).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60)));
}

function getModalityLabel(type: string): string {
  switch (type) {
    case 'group_coaching': return 'Group Coaching';
    case 'one_on_one': return '1:1 Coaching';
    case 'workshop': return 'Workshops';
    default: return type;
  }
}

function getModalityIcon(type: string, size = 16) {
  switch (type) {
    case 'group_coaching':
    case 'group_session': return <Users size={size} />;
    case 'one_on_one':
    case 'one_on_one_session': return <User size={size} />;
    case 'workshop':
    case 'workshop_session': return <BookOpen size={size} />;
    default: return <Calendar size={size} />;
  }
}

function getProgressPercent(modality: ParticipantModality): number {
  if (!modality.session_count || modality.session_count === 0) return 0;
  return Math.round((modality.completed_count / modality.session_count) * 100);
}

// ============================================================================
// Welcome Banner (AC6)
// ============================================================================

function WelcomeBanner({ firstName, cohortName }: { firstName: string; cohortName: string }) {
  return (
    <Box mb="xl">
      <Title order={2} fw={700}>
        Welcome back, {firstName}
      </Title>
      <Text c="dimmed" size="md" mt={4}>
        Here's what's happening with your {cohortName} program.
      </Text>
    </Box>
  );
}

// ============================================================================
// Live Session Banner (MED-950)
//
// Surfaces a session currently in `in_progress` status that the participant is
// enrolled in. Renders independently of modality announcement gating so a
// launched session is always joinable, even if the parent modality was never
// formally announced. The Join CTA enters the waiting-room route, which polls
// and auto-redirects into the active session.
// ============================================================================

function LiveSessionBanner({ session }: { session: ParticipantLiveSession }) {
  const startedAt = session.actual_start ?? session.scheduled_start;
  const minutesAgo = startedAt
    ? Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 60000))
    : null;
  const startedLabel =
    minutesAgo === null
      ? null
      : minutesAgo === 0
        ? 'Just started'
        : `Started ${minutesAgo} min ago`;
  const metaLine = [startedLabel, session.group_name].filter(Boolean).join(' · ');

  return (
    <Card
      radius="md"
      mb="lg"
      style={{ backgroundColor: '#2D4739', border: 'none' }}
      padding="lg"
    >
      <Group justify="space-between" wrap="nowrap" gap="md" className="med-mobile-wrap">
        <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <ThemeIcon
            size={44}
            radius="xl"
            style={{ backgroundColor: 'rgba(255,255,255,0.15)', flexShrink: 0 }}
          >
            <Zap size={20} color="white" />
          </ThemeIcon>
          <Box style={{ minWidth: 0 }}>
            <Text
              size="xs"
              fw={600}
              c="rgba(255,255,255,0.7)"
              tt="uppercase"
              style={{ letterSpacing: '0.5px' }}
              mb={2}
            >
              Session in Progress
            </Text>
            <Text fw={600} c="white" size="md" truncate>
              {session.title}
            </Text>
            {metaLine && (
              <Text size="xs" c="rgba(255,255,255,0.7)" mt={2}>
                {metaLine}
              </Text>
            )}
          </Box>
        </Group>

        <Group gap="md" wrap="nowrap" style={{ flexShrink: 0 }}>
          <Button
            component={Link}
            to={`/participant/sessions/${session.id}/waiting-room`}
            size="sm"
            rightSection={<ArrowRight size={14} />}
            style={{ backgroundColor: '#4A7C59', border: 'none' }}
          >
            Join Now
          </Button>
        </Group>
      </Group>
    </Card>
  );
}

// ============================================================================
// Starting Soon Banner (AC7, AC10a, AC16)
// ============================================================================

function StartingSoonBanner({ session }: { session: ParticipantUpcomingSession }) {
  const minutesAway = getCountdownMinutes(session.scheduled_start);
  // MED-1045 — a session leader is never routed to the participant waiting room;
  // their CTA is "Prepare to start" → the pre-session reflection/prep flow.
  // (A flag-on peer leader normally sees DashboardRefreshView, but this legacy
  // banner shares the same `starting_soon` payload, so we gate it identically to
  // avoid leaving the same-class gap behind.)
  const isLeading = !!session.is_leading;
  const ctaTo = isLeading
    ? `/participant/sessions/${session.id}/reflection`
    : `/participant/sessions/${session.id}/waiting-room`;
  const ctaLabel = isLeading ? 'Prepare to start' : 'Enter waiting room';

  return (
    <Card
      radius="md"
      mb="lg"
      style={{ backgroundColor: '#2D4739', border: 'none' }}
      padding="lg"
    >
      <Group justify="space-between" wrap="nowrap" gap="md" className="med-mobile-wrap">
        <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <ThemeIcon
            size={44}
            radius="xl"
            style={{ backgroundColor: 'rgba(255,255,255,0.15)', flexShrink: 0 }}
          >
            <Zap size={20} color="white" />
          </ThemeIcon>
          <Box style={{ minWidth: 0 }}>
            <Text size="xs" fw={600} c="rgba(255,255,255,0.7)" tt="uppercase" style={{ letterSpacing: '0.5px' }} mb={2}>
              Starting Soon
            </Text>
            <Text fw={600} c="white" size="md" truncate>
              {session.title}
            </Text>
            <Text size="xs" c="rgba(255,255,255,0.7)" mt={2}>
              {formatDate(session.scheduled_start)} · {formatTime(session.scheduled_start)}
              {session.group_name ? ` · ${session.group_name}` : ''}
            </Text>
          </Box>
        </Group>

        <Group gap="md" wrap="nowrap" style={{ flexShrink: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Clock size={14} color="rgba(255,255,255,0.7)" />
            <Text size="sm" c="rgba(255,255,255,0.85)" fw={500} style={{ whiteSpace: 'nowrap' }}>
              Starts in {minutesAway} min
            </Text>
          </Group>
          {/* MED-804 QA fix: the scheduled-state CTA enters the in-app waiting
              room (the pre-start holding surface), not Zoom directly. The
              waiting room polls and auto-redirects into the active session when
              the leader opens it, and surfaces the Zoom "Join video call" link
              from inside. Always rendered — entering the waiting room does not
              require a zoom_link. */}
          <Button
            component={Link}
            to={ctaTo}
            size="sm"
            rightSection={<ArrowRight size={14} />}
            style={{ backgroundColor: '#4A7C59', border: 'none' }}
          >
            {ctaLabel}
          </Button>
        </Group>
      </Group>
    </Card>
  );
}

// ============================================================================
// Action Items Section (AC8, AC17)
// ============================================================================

// MED-699 AC13: Intro → Midpoint → 1:1 Coaching (null huddle_type)
const ACTION_ITEM_ORDER: Record<string, number> = {
  intro: 0,
  midpoint_checkin: 1,
  null: 2,
};

function getActionItemCopy(item: ParticipantActionItem): { title: string; subtitle: string } {
  if (item.huddle_type === 'intro') {
    return {
      title: 'Schedule your Intro Check-in',
      subtitle: `A required kickoff conversation with ${item.first_name} ${item.last_name}`,
    };
  }
  if (item.huddle_type === 'midpoint_checkin') {
    return {
      title: 'Schedule your Midpoint Check-in',
      subtitle: `A required progress check-in with ${item.first_name} ${item.last_name}`,
    };
  }
  return {
    title: 'Schedule your 1:1 coaching',
    subtitle: `${item.unscheduled_count} session${item.unscheduled_count !== 1 ? 's' : ''} with ${item.first_name} ${item.last_name} awaiting scheduling`,
  };
}

function ActionItemsSection({
  items,
  onScheduleCoaching,
  onScheduleCheckin,
}: {
  items: ParticipantActionItem[];
  onScheduleCoaching: (coachId: string) => void;
  onScheduleCheckin: (coachId: string, huddleType: 'intro' | 'midpoint_checkin') => void;
}) {
  if (items.length === 0) return null;

  // MED-699 AC13: sort Intro → Midpoint → 1:1 coaching (null)
  const sortedItems = [...items].sort(
    (a, b) =>
      (ACTION_ITEM_ORDER[a.huddle_type ?? 'null'] ?? 99) -
      (ACTION_ITEM_ORDER[b.huddle_type ?? 'null'] ?? 99),
  );

  return (
    <Box mb="lg">
      <Text
        size="xs"
        fw={600}
        tt="uppercase"
        c="dimmed"
        mb="sm"
        style={{ letterSpacing: '0.5px' }}
      >
        Action Items
      </Text>
      <Card withBorder radius="md" padding="md">
        <Stack gap="xs">
          {sortedItems.map(item => {
            const { title, subtitle } = getActionItemCopy(item);
            const key = `${item.coach_id}:${item.huddle_type ?? 'null'}`;
            const isTyped =
              item.huddle_type === 'intro' || item.huddle_type === 'midpoint_checkin';
            // MED-845: Gate Schedule CTA on the assigned coach having connected Calendly.
            // Applies to all action item types (1:1 coaching, intro, midpoint_checkin).
            const awaitingCoachSchedule = !item.coach_calendly_connected;
            return (
              <Group key={key} justify="space-between" wrap="nowrap" className="med-mobile-wrap">
                <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                  <ThemeIcon size={32} radius="xl" variant="light" color="gray">
                    {isTyped ? <MessageSquare size={16} /> : <User size={16} />}
                  </ThemeIcon>
                  <Box>
                    <Text size="sm" fw={500}>
                      {title}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {subtitle}
                    </Text>
                  </Box>
                </Group>
                <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
                  {awaitingCoachSchedule && (
                    <Tooltip
                      label={AWAITING_COACH_TOOLTIP_COPY}
                      withArrow
                      position="top"
                      multiline
                      w={260}
                    >
                      <Badge size="sm" variant="light" color="gray">
                        {AWAITING_COACH_SCHEDULE_LABEL}
                      </Badge>
                    </Tooltip>
                  )}
                  <Button
                    size="xs"
                    color="medleyGreen"
                    disabled={awaitingCoachSchedule}
                    onClick={() =>
                      isTyped
                        ? onScheduleCheckin(
                            item.coach_id,
                            item.huddle_type as 'intro' | 'midpoint_checkin',
                          )
                        : onScheduleCoaching(item.coach_id)
                    }
                  >
                    Schedule
                  </Button>
                </Group>
              </Group>
            );
          })}
        </Stack>
      </Card>
    </Box>
  );
}

// ============================================================================
// Upcoming Sessions Section (AC9, AC10)
// ============================================================================

// ============================================================================
// Peer Leader Callout (MED-813 / E4.1, AC #1)
// ============================================================================
// Shown when the participant is the designated leader of an upcoming peer-led
// session. CTA links to the pre-session page for that session, where the full
// peer-leader banner + session prep entry point live.
function PeerLeaderCallout({ callout }: { callout: ParticipantPeerLeaderCallout }) {
  const sessionLabel =
    callout.session_number != null ? `Session ${callout.session_number}` : 'your session';
  return (
    <Card
      radius="md"
      mb="lg"
      style={{ backgroundColor: '#2D4739', border: 'none' }}
      padding="lg"
    >
      <Group justify="space-between" wrap="nowrap" gap="md" className="med-mobile-wrap">
        <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <ThemeIcon
            size={44}
            radius="xl"
            style={{ backgroundColor: 'rgba(255,255,255,0.15)', flexShrink: 0 }}
          >
            <Mic size={20} color="white" />
          </ThemeIcon>
          <Box style={{ minWidth: 0 }}>
            <Text
              size="xs"
              fw={600}
              c="rgba(255,255,255,0.7)"
              tt="uppercase"
              style={{ letterSpacing: '0.5px' }}
              mb={2}
            >
              You&apos;re leading {sessionLabel}
            </Text>
            <Text fw={600} c="white" size="md" truncate>
              {callout.title}
            </Text>
            <Text size="xs" c="rgba(255,255,255,0.7)" mt={2}>
              {formatDate(callout.scheduled_start)} · {formatTime(callout.scheduled_start)}
              {callout.group_name ? ` · ${callout.group_name}` : ''}
            </Text>
          </Box>
        </Group>
        <Button
          component={Link}
          to={`/participant/sessions/${callout.id}/reflection`}
          size="sm"
          rightSection={<ArrowRight size={14} />}
          style={{ backgroundColor: '#4A7C59', border: 'none', flexShrink: 0 }}
        >
          Start Session Prep
        </Button>
      </Group>
    </Card>
  );
}

// MED-1394: an Upcoming Sessions 1:1 or check-in row opens its own row further
// down the page, where the Reschedule and Cancel menu lives.
const oneOnOneRowId = (sessionId: string) => `one-on-one-session-${sessionId}`;

function focusOneOnOneRow(sessionId: string) {
  const row = document.getElementById(oneOnOneRowId(sessionId));
  if (!row) return;
  row.scrollIntoView({ behavior: 'smooth', block: 'center' });
  // The row's only button is its Reschedule/Cancel menu, when it has one. Open it.
  const menuButton = row.querySelector<HTMLButtonElement>('button');
  (menuButton ?? row).focus({ preventScroll: true });
  menuButton?.click();
}

function UpcomingSessionsSection({
  sessions,
  liveSessionDeliveryEnabled,
}: {
  sessions: ParticipantUpcomingSession[];
  /** MED-1181: combined flag + cohort-modality LSD gate for the "Reflect" link. */
  liveSessionDeliveryEnabled: boolean;
}) {
  return (
    <Box mb="lg">
      <Group justify="space-between" mb="sm">
        <Text
          size="xs"
          fw={600}
          tt="uppercase"
          c="dimmed"
          style={{ letterSpacing: '0.5px' }}
        >
          Upcoming Sessions
        </Text>
      </Group>

      <Card withBorder radius="md" padding="md">
        {sessions.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            No upcoming sessions scheduled.
          </Text>
        ) : (
          <Stack gap={0}>
            {sessions.map((session, i) => {
              const opensOneOnOne =
                session.modality_type === 'one_on_one' ||
                isCheckinOneOnOneType(session.one_on_one_type);
              // MED-1350: a check-in reads "Intro check-in", not its stored title.
              const rowTitle = isCheckinOneOnOneType(session.one_on_one_type)
                ? getOneOnOneTypeConfig(session.one_on_one_type).displayLabel
                : session.title;
              return (
              <Box key={session.id}>
                {i > 0 && <Divider my="xs" />}
                <Group
                  justify="space-between"
                  wrap="nowrap"
                  py="xs"
                  className="med-mobile-wrap"
                  {...(opensOneOnOne && {
                    role: 'button',
                    tabIndex: 0,
                    'aria-label': `${rowTitle}, show booking actions`,
                    style: { cursor: 'pointer' },
                    onClick: () => focusOneOnOneRow(session.id),
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        focusOneOnOneRow(session.id);
                      }
                    },
                  })}
                >
                  <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                    <ThemeIcon size={32} radius="md" variant="light" color="gray">
                      {getModalityIcon(session.modality_type)}
                    </ThemeIcon>
                    <Box style={{ minWidth: 0 }}>
                      <Text size="sm" fw={500} truncate>
                        {rowTitle}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {formatDate(session.scheduled_start)} · {formatTime(session.scheduled_start)}
                        {session.duration_minutes ? ` · ${session.duration_minutes / 60 >= 1 ? `${session.duration_minutes / 60} hour${session.duration_minutes / 60 !== 1 ? 's' : ''}` : `${session.duration_minutes} min`}` : ''}
                        {/* MED-1350: a 1:1 names who runs it. */}
                        {session.modality_type === 'one_on_one' && session.coach_name
                          ? ` · with ${session.coach_name}`
                          : ''}
                      </Text>
                    </Box>
                  </Group>
                  <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                    {/* MED-803: Pre-Session Reflection link for group sessions still in the open window.
                        MED-1181: pre-session reflection is an LSD affordance — gate on the cohort LSD modality. */}
                    {liveSessionDeliveryEnabled &&
                      session.modality_type === 'group_coaching' &&
                      new Date(session.scheduled_start).getTime() > Date.now() && (
                        <Button
                          size="xs"
                          variant="subtle"
                          color="dark"
                          component={Link}
                          to={`/participant/sessions/${session.id}/reflection`}
                        >
                          Reflect
                        </Button>
                      )}
                    <Badge
                      size="xs"
                      variant="light"
                      color="gray"
                      style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
                    >
                      {getRelativeLabel(session.scheduled_start)}
                    </Badge>
                  </Group>
                </Group>
              </Box>
              );
            })}
          </Stack>
        )}
      </Card>
    </Box>
  );
}

// ============================================================================
// My Program Section (AC11, AC12)
// ============================================================================

function MyProgramSection({
  cohortName,
  partnerName,
  startDate,
  endDate,
  modalities,
}: {
  cohortName: string;
  partnerName: string | null;
  startDate: string | null;
  endDate: string | null;
  modalities: ParticipantModality[];
}) {
  const announced = modalities.filter(m =>
    ['group_coaching', 'one_on_one', 'workshop'].includes(m.modality_type as ModalityType),
  );

  return (
    <Box mb="lg">
      <Text
        size="xs"
        fw={600}
        tt="uppercase"
        c="dimmed"
        mb="sm"
        style={{ letterSpacing: '0.5px' }}
      >
        My Program
      </Text>
      <Card withBorder radius="md" padding="md">
        <Text fw={600} size="sm" mb={2}>
          {cohortName}
        </Text>
        <Text size="xs" c="dimmed" mb="md">
          {[partnerName, formatDateRange(startDate, endDate)].filter(Boolean).join(' · ')}
        </Text>

        {announced.length === 0 ? (
          <Text size="sm" c="dimmed">
            No modalities announced yet.
          </Text>
        ) : (
          <Group grow gap="md" align="flex-start" className="med-mobile-program">
            {announced.map(m => {
              const pct = getProgressPercent(m);
              return (
                <Box key={m.id}>
                  <Text size="xs" fw={500} mb={4}>
                    {getModalityLabel(m.modality_type)}
                  </Text>
                  <Text size="xs" c="dimmed" mb={6}>
                    {m.completed_count}/{m.session_count ?? '?'}
                  </Text>
                  <Progress
                    value={pct}
                    color="medleyGreen"
                    size="sm"
                    radius="xl"
                    mb={4}
                  />
                  <Text size="xs" c="dimmed">
                    {pct}% complete
                  </Text>
                </Box>
              );
            })}
          </Group>
        )}
      </Card>
    </Box>
  );
}

// ============================================================================
// Empty State — No Modalities Announced (AC14)
// ============================================================================

function NoModalitiesState() {
  return (
    <Card withBorder radius="md" padding="xl" ta="center">
      <ThemeIcon size={48} radius="xl" variant="light" color="gray" mx="auto" mb="md">
        <Video size={24} />
      </ThemeIcon>
      <Text fw={600} mb="xs">
        Your program is being set up
      </Text>
      <Text size="sm" c="dimmed">
        Sessions and modalities will appear here once your program manager finalizes and announces
        them. Check back soon.
      </Text>
    </Card>
  );
}

// ============================================================================
// Workshops Section (MED-569)
// ============================================================================

type AttendanceBadgeStatus = 'upcoming' | 'attended' | 'missed';

function getAttendanceBadgeStatus(
  status: WorkshopAttendanceStatus | null,
  scheduledStart: string | null,
): AttendanceBadgeStatus {
  if (status === 'attended' || status === 'present') return 'attended';
  if (status === 'absent' || status === 'excused' || status === 'late' || status === 'partial') return 'missed';
  // pending or null — determine by date
  if (scheduledStart && new Date(scheduledStart) < new Date()) return 'missed';
  return 'upcoming';
}

function WorkshopAttendanceBadge({ status, scheduledStart }: {
  status: WorkshopAttendanceStatus | null;
  scheduledStart: string | null;
}) {
  const badge = getAttendanceBadgeStatus(status, scheduledStart);

  if (badge === 'attended') {
    return (
      <Badge
        size="xs"
        color="medleyGreen"
        variant="filled"
        leftSection={<Check size={10} />}
        style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
      >
        Attended
      </Badge>
    );
  }
  if (badge === 'missed') {
    return (
      <Badge
        size="xs"
        color="dangerRed"
        variant="light"
        style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
      >
        Missed
      </Badge>
    );
  }
  return (
    <Badge
      size="xs"
      color="gray"
      variant="light"
      style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
    >
      Upcoming
    </Badge>
  );
}

function WorkshopResourceChip({ resource }: { resource: ParticipantWorkshopResource }) {
  const label = resource.original_filename ?? resource.title;
  const isLink = resource.resource_type === 'link';
  const icon = isLink ? <LinkIcon size={12} /> : <File size={12} />;

  if (!resource.url) {
    return (
      <Badge
        size="sm"
        variant="light"
        color="gray"
        leftSection={icon}
        style={{ fontWeight: 500, cursor: 'default' }}
      >
        {label}
      </Badge>
    );
  }

  return (
    <Badge
      component="a"
      href={resource.url}
      target="_blank"
      rel="noopener noreferrer"
      size="sm"
      variant="light"
      color="blue"
      leftSection={icon}
      style={{ fontWeight: 500, cursor: 'pointer', textDecoration: 'none' }}
    >
      {label}
    </Badge>
  );
}

function WorkshopCard({ workshop }: { workshop: ParticipantWorkshop }) {
  const displayNumber = workshop.sequence_number ?? workshop.session_number;
  const title = displayNumber ? `${displayNumber}. ${workshop.title}` : workshop.title;

  return (
    <Card
      withBorder
      radius="md"
      padding="md"
      style={{ flex: '1 1 280px', minWidth: 0 }}
    >
      <Stack gap="xs">
        {/* Title + badge */}
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
          <Text fw={600} size="sm" style={{ flex: 1, minWidth: 0 }}>
            {title}
          </Text>
          <WorkshopAttendanceBadge
            status={workshop.attendance_status}
            scheduledStart={workshop.scheduled_start}
          />
        </Group>

        {/* Date / time / duration */}
        {workshop.scheduled_start && (
          <Group gap={4} wrap="nowrap">
            <Calendar size={13} color="#888" style={{ flexShrink: 0 }} />
            <Text size="xs" c="dimmed">
              {formatDate(workshop.scheduled_start)}
              {' · '}
              {formatTime(workshop.scheduled_start)}
              {workshop.duration_minutes
                ? ` · ${workshop.duration_minutes >= 60
                    ? `${workshop.duration_minutes / 60} hr`
                    : `${workshop.duration_minutes} min`}`
                : ''}
            </Text>
          </Group>
        )}

        {/* Facilitator */}
        {workshop.facilitator_name && (
          <Group gap={4} wrap="nowrap">
            <User size={13} color="#888" style={{ flexShrink: 0 }} />
            <Text size="xs" c="dimmed">{workshop.facilitator_name}</Text>
          </Group>
        )}

        {/* Resources */}
        {workshop.resources.length > 0 && (
          <>
            <Divider />
            <Group gap="xs" wrap="wrap">
              {workshop.resources.map(r => (
                <WorkshopResourceChip key={r.id} resource={r} />
              ))}
            </Group>
          </>
        )}
      </Stack>
    </Card>
  );
}

function WorkshopsSection({ workshops }: { workshops: ParticipantWorkshop[] }) {
  if (workshops.length === 0) return null;

  return (
    <Box mb="lg">
      <Text
        size="xs"
        fw={600}
        tt="uppercase"
        c="dimmed"
        mb="sm"
        style={{ letterSpacing: '0.5px' }}
      >
        Workshops
      </Text>
      <Group gap="md" align="stretch" wrap="wrap">
        {workshops.map(w => (
          <WorkshopCard key={w.id} workshop={w} />
        ))}
      </Group>
    </Box>
  );
}

// ============================================================================
// Group Coaching Section (E22-3 — MED-607)
// ============================================================================

function GroupCoachingSessionRow({
  session,
}: {
  session: ParticipantGroupCoachingSession;
}) {
  const isCompleted = session.status === 'completed';

  return (
    <Group justify="space-between" wrap="nowrap" py="sm" px="md" className="med-mobile-wrap">
      <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
        <ThemeIcon
          size={36}
          radius="xl"
          color="medleyGreen"
          variant="filled"
        >
          <Text size="sm" fw={700} c="white">
            {session.session_number ?? '–'}
          </Text>
        </ThemeIcon>
        <Box style={{ minWidth: 0 }}>
          <Text size="sm" fw={600} truncate>
            {session.title}
          </Text>
          {session.scheduled_start && (
            <Text size="xs" c="dimmed">
              {formatDate(session.scheduled_start)} &middot; {formatTime(session.scheduled_start)}
            </Text>
          )}
        </Box>
      </Group>

      <Group gap="lg" wrap="nowrap" style={{ flexShrink: 0 }}>
        {isCompleted && session.attendance_percent != null && (
          <Box ta="center">
            <Text size="sm" fw={600}>{session.attendance_percent}%</Text>
            <Text size="xs" c="dimmed">Attended</Text>
          </Box>
        )}
        {isCompleted && session.avg_rating != null && (
          <Box ta="center">
            <Text size="sm" fw={600}>{session.avg_rating}</Text>
            <Text size="xs" c="dimmed">Rating</Text>
          </Box>
        )}
        {isCompleted && (
          <Badge
            size="xs"
            color="gray"
            variant="light"
            style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
          >
            Completed
          </Badge>
        )}
        {/* MED-1360: a past session the coach has not marked is no longer scheduled. */}
        {isPastAwaitingAttendance(session) ? (
          <AttendancePendingBadge />
        ) : (
          session.status === 'scheduled' && (
            <Badge
              size="xs"
              color="medleyGreen"
              variant="light"
              style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
            >
              Scheduled
            </Badge>
          )
        )}
      </Group>
    </Group>
  );
}

function CheckinCtaCard({
  coaching,
  onSchedule,
  onReschedule,
  onCancel,
}: {
  coaching: ParticipantGroupCoaching;
  onSchedule: () => void;
  onReschedule: () => void;
  onCancel: () => void;
}) {
  const coach = coaching.coach;
  if (!coach) return null;

  // AC16: Coach has no Calendly — show fallback
  if (!coach.calendly_connected || !coach.checkin_event_configured) {
    return (
      <Box
        p="md"
        style={{
          borderBottom: '1px solid #E8E6E1',
        }}
      >
        <Group justify="space-between" wrap="nowrap" gap="md" className="med-mobile-callout">
          <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <ThemeIcon size={40} radius="md" variant="light" color="gray">
              <MessageSquare size={20} />
            </ThemeIcon>
            <Box style={{ minWidth: 0 }}>
              <Text size="sm" fw={600}>
                Need to talk through something?
              </Text>
              <Text size="xs" c="dimmed">
                Contact your coach to schedule a check-in
              </Text>
            </Box>
          </Group>
          {coach.email && (
            <Text
              size="sm"
              fw={500}
              c="medleyGreen"
              component="a"
              href={`mailto:${coach.email}`}
              style={{ textDecoration: 'none', flexShrink: 0 }}
            >
              {coach.email}
            </Text>
          )}
        </Group>
      </Box>
    );
  }

  // AC17: Already has active check-in
  if (coaching.active_checkin) {
    return (
      <Box
        p="md"
        style={{
          borderBottom: '1px solid #E8E6E1',
        }}
      >
        <Group justify="space-between" wrap="nowrap" gap="md" className="med-mobile-callout">
          <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <ThemeIcon size={40} radius="md" variant="light" color="medleyGreen">
              <Check size={20} />
            </ThemeIcon>
            <Box style={{ minWidth: 0 }}>
              <Text size="sm" fw={600}>
                Check-in scheduled
              </Text>
              <Text size="xs" c="dimmed">
                {coaching.active_checkin.scheduled_start
                  ? `${formatDate(coaching.active_checkin.scheduled_start)} · ${formatTime(coaching.active_checkin.scheduled_start)}`
                  : 'Pending confirmation'}
                {' with '}
                {coach.first_name} {coach.last_name}
              </Text>
            </Box>
          </Group>
          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
            {coaching.active_checkin.video_link && (
              <Button
                component="a"
                href={coaching.active_checkin.video_link}
                target="_blank"
                rel="noopener noreferrer"
                size="xs"
                variant="outline"
                color="medleyGreen"
              >
                Join Meeting
              </Button>
            )}
            {/* AC1/AC9 + MED-840 AC3/AC16: Reschedule/Cancel via shared CheckinActionsMenu.
                MED-1360: only before the check-in time passes. */}
            {canChangeBooking(coaching.active_checkin) && (
              <CheckinActionsMenu onReschedule={onReschedule} onCancel={onCancel} />
            )}
          </Group>
        </Group>
      </Box>
    );
  }

  // Default: can schedule check-in
  return (
    <Box
      p="md"
      style={{
        borderBottom: '1px solid #E8E6E1',
      }}
    >
      <Group justify="space-between" wrap="nowrap" gap="md">
        <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <ThemeIcon size={40} radius="md" variant="light" color="gray">
            <MessageSquare size={20} />
          </ThemeIcon>
          <Box style={{ minWidth: 0 }}>
            <Text size="sm" fw={600}>
              Need to talk through something?
            </Text>
            <Text size="xs" c="dimmed">
              Book a quick check-in with {coach.first_name} {coach.last_name}
            </Text>
          </Box>
        </Group>
        <Button
          size="sm"
          variant="outline"
          color="dark"
          leftSection={<Calendar size={14} />}
          onClick={onSchedule}
          style={{ flexShrink: 0 }}
        >
          Schedule 1:1 Coach Check-In
        </Button>
      </Group>
    </Box>
  );
}

/**
 * MED-1350: the check-in schedule, reschedule and cancel state, lifted out of
 * GroupCoachingSection. A check-in is a 1:1, so its rows show in the 1:1
 * section when the cohort has one, and in Group Coaching when it does not.
 * The dashboard calls this once and renders `modals` once, so both sections
 * share one set of dialogs.
 */
interface CheckinActions {
  openCheckinModal: (type: ParticipantHuddleType) => void;
  rescheduleCheckin: (
    id: string,
    huddleType: ParticipantHuddleType,
    schedulingMethod: string | null | undefined,
  ) => void;
  cancelCheckin: (target: { id: string; scheduledStart: string | null; label: string }) => void;
  /** The Intro or Midpoint row, or null when the participant has none. */
  renderCheckinRow: (type: 'intro' | 'midpoint_checkin') => ReactNode;
  modals: ReactNode;
}

function useCheckinActions(
  coaching: ParticipantGroupCoaching | null,
  cohortId: string,
  cohortName: string,
  onRefresh: () => void,
): CheckinActions {
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [checkinModalType, setCheckinModalType] = useState<ParticipantHuddleType>('general');
  // MED-840: Per-row reschedule/cancel targets — replace single-active-checkin booleans
  // so Intro / Midpoint / General check-ins each scope to their own row.
  // MED-1365 QA round 3: the row's huddle type, so the reschedule modal names
  // the Intro or Midpoint check-in instead of falling back to 'general'.
  const [rescheduleTarget, setRescheduleTarget] = useState<{
    id: string;
    huddleType: ParticipantHuddleType;
    /** QA round 4: how it was booked, for the confirm text. */
    schedulingMethod: string | null | undefined;
    /** Round 4 review: the check-in's own coach, which the reschedule books with. */
    coach: ParticipantGroupCoachingCoach | null;
  } | null>(null);
  const [cancelTarget, setCancelTarget] = useState<{
    id: string;
    scheduledStart: string | null;
    label: string;
  } | null>(null);
  const [isCancelingCheckin, setIsCancelingCheckin] = useState(false);
  const [checkinCancelError, setCheckinCancelError] = useState<string | null>(null);

  const openCheckinModal = (type: ParticipantHuddleType) => {
    setCheckinModalType(type);
    setShowCheckinModal(true);
  };

  // MED-699: compute disabled types + reasons for modal dropdown (AC17)
  const typed = coaching?.typed_checkins;
  const disabledHuddleTypes: ParticipantHuddleType[] = [];
  const disabledHuddleTypeReasons: Partial<Record<ParticipantHuddleType, string>> = {};
  (['intro', 'midpoint_checkin'] as const).forEach(t => {
    const c = t === 'intro' ? typed?.intro : typed?.midpoint;
    if (c?.status === 'scheduled') {
      disabledHuddleTypes.push(t);
      disabledHuddleTypeReasons[t] = 'Already scheduled';
    } else if (c?.status === 'completed') {
      disabledHuddleTypes.push(t);
      disabledHuddleTypeReasons[t] = 'Already completed';
    }
  });
  if (coaching?.active_checkin) {
    disabledHuddleTypes.push('general');
    disabledHuddleTypeReasons.general = 'Already scheduled';
  }

  // AC18: coach Calendly fallback — reused across CTA card AND typed line items
  const canScheduleAny =
    !!coaching?.coach?.calendly_connected && !!coaching?.coach?.checkin_event_configured;

  // MED-840: Generalized cancel handler — operates on whichever check-in (intro/midpoint/general)
  // was clicked. Backend (ParticipantPortalController.cancelSession) now treats all three huddle
  // types as check-ins → status transitions to 'cancelled'.
  const handleCancelCheckin = async () => {
    if (!cancelTarget) return;
    setIsCancelingCheckin(true);
    setCheckinCancelError(null);
    try {
      await participantPortalApi.cancelSession(cancelTarget.id);
      setCancelTarget(null);
      onRefresh();
    } catch (err) {
      setCheckinCancelError(
        err instanceof Error ? err.message : 'Failed to cancel check-in. Please try again.',
      );
    } finally {
      setIsCancelingCheckin(false);
    }
  };

  const closeCheckinCancelModal = () => {
    setCancelTarget(null);
    setCheckinCancelError(null);
  };

  const renderCheckinRow = (type: 'intro' | 'midpoint_checkin'): ReactNode => {
    const checkin = type === 'intro' ? typed?.intro : typed?.midpoint;
    if (!coaching || !checkin) return null;
    // MED-1394: checkin.id is the session id, the target of its Upcoming Sessions row.
    return (
      <Box id={oneOnOneRowId(checkin.id)} tabIndex={-1}>
      <CheckinLineItem
        huddleType={type}
        checkin={checkin}
        canSchedule={canScheduleAny && checkin.status === 'unscheduled'}
        coachEmail={coaching.coach?.email ?? null}
        coachCalendlyConnected={coaching.coach?.calendly_connected ?? false}
        onSchedule={() => openCheckinModal(type)}
        onReschedule={() =>
          setRescheduleTarget({
            id: checkin.id,
            huddleType: type,
            schedulingMethod: checkin.scheduling_method,
            coach: checkin.coach ?? coaching.coach,
          })
        }
        onCancel={() =>
          setCancelTarget({
            id: checkin.id,
            scheduledStart: checkin.scheduled_start,
            label: type === 'intro' ? 'your Intro Check-in' : 'your Midpoint Check-in',
          })
        }
      />
      </Box>
    );
  };

  const modals = coaching ? (
    <>
      {/* Check-in scheduling modal — MED-699: type-aware */}
      {coaching.coach && showCheckinModal && (
        <ScheduleCheckinModal
          opened={showCheckinModal}
          onClose={() => setShowCheckinModal(false)}
          onSuccess={() => {
            setShowCheckinModal(false);
            onRefresh();
          }}
          coach={coaching.coach}
          cohortId={cohortId}
          cohortName={cohortName}
          initialHuddleType={checkinModalType}
          disabledHuddleTypes={disabledHuddleTypes}
          disabledHuddleTypeReasons={disabledHuddleTypeReasons}
        />
      )}

      {/* Check-in reschedule modal (E22-4 + MED-840: scoped to per-row check-in id) */}
      {rescheduleTarget?.coach && (
        <ScheduleCheckinModal
          opened={!!rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          onSuccess={() => {
            setRescheduleTarget(null);
            onRefresh();
          }}
          coach={rescheduleTarget.coach}
          cohortId={cohortId}
          cohortName={cohortName}
          rescheduleMode
          activeCheckinId={rescheduleTarget.id}
          initialHuddleType={rescheduleTarget.huddleType}
          activeCheckinSchedulingMethod={rescheduleTarget.schedulingMethod}
        />
      )}

      {/* Check-in cancel dialog (E22-4 AC10 + MED-840: scoped to per-row check-in id) */}
      <CancelConfirmationModal
        opened={!!cancelTarget}
        onClose={closeCheckinCancelModal}
        onConfirm={handleCancelCheckin}
        isLoading={isCancelingCheckin}
        sessionLabel={cancelTarget?.label ?? 'your coach check-in'}
        scheduledStart={cancelTarget?.scheduledStart ?? null}
        error={checkinCancelError}
      />
    </>
  ) : null;

  return {
    openCheckinModal,
    rescheduleCheckin: (id, huddleType, schedulingMethod) =>
      setRescheduleTarget({ id, huddleType, schedulingMethod, coach: coaching?.coach ?? null }),
    cancelCheckin: setCancelTarget,
    renderCheckinRow,
    modals,
  };
}

function GroupCoachingSection({
  coaching,
  checkins,
  showTypedCheckins,
}: {
  coaching: ParticipantGroupCoaching;
  checkins: CheckinActions;
  /** MED-1350: false when the 1:1 section shows the Intro and Midpoint rows. */
  showTypedCheckins: boolean;
}) {
  const typed = showTypedCheckins ? coaching.typed_checkins : undefined;

  // Midpoint placement index (AC2): render AFTER sessions[midpoint_session_index]
  const midpointIndex = typed?.midpoint?.midpoint_session_index ?? null;

  return (
    <Box mb="lg">
      <Group justify="space-between" mb="sm">
        <Group gap="sm" align="baseline">
          <Text
            size="xs"
            fw={600}
            tt="uppercase"
            c="dimmed"
            style={{ letterSpacing: '0.5px' }}
          >
            Group Coaching
          </Text>
          <Text size="xs" c="dimmed">
            {coaching.group_name}
          </Text>
        </Group>
      </Group>

      <Card withBorder radius="md" padding={0}>
        {/* Check-in CTA (AC1, AC2, AC16, AC17) */}
        <CheckinCtaCard
          coaching={coaching}
          onSchedule={() => checkins.openCheckinModal('general')}
          onReschedule={() =>
            coaching.active_checkin &&
            checkins.rescheduleCheckin(
              coaching.active_checkin.id,
              'general',
              coaching.active_checkin.scheduling_method,
            )
          }
          onCancel={() =>
            coaching.active_checkin &&
            checkins.cancelCheckin({
              id: coaching.active_checkin.id,
              scheduledStart: coaching.active_checkin.scheduled_start,
              label: 'your coach check-in',
            })
          }
        />

        {/* Session list with MED-699 Intro + Midpoint line items spliced in */}
        {coaching.sessions.length === 0 && !typed?.intro && !typed?.midpoint ? (
          <Box py="lg">
            <Text size="sm" c="dimmed" ta="center">
              No group coaching sessions yet.
            </Text>
            {/* MED-1394: the next step. */}
            <Text size="xs" c="dimmed" ta="center" mt={4}>
              Sessions appear here once your coach schedules them.
            </Text>
          </Box>
        ) : (
          <Stack gap={0}>
            {/* MED-699 AC1: Intro row pinned above session 1 */}
            {typed?.intro && (
              <>
                <Divider />
                {checkins.renderCheckinRow('intro')}
              </>
            )}

            {coaching.sessions.map((session, i) => (
              <Box key={session.id}>
                <Divider />
                <GroupCoachingSessionRow session={session} />
                {/* MED-699 AC2: Midpoint row after sessions[midpoint_session_index] */}
                {midpointIndex !== null && i === midpointIndex && typed?.midpoint && (
                  <>
                    <Divider />
                    {checkins.renderCheckinRow('midpoint_checkin')}
                  </>
                )}
              </Box>
            ))}

            {/* Fallback: Midpoint placeholder exists but target session index is out of range */}
            {typed?.midpoint &&
              (midpointIndex === null || midpointIndex >= coaching.sessions.length) && (
                <>
                  <Divider />
                  {checkins.renderCheckinRow('midpoint_checkin')}
                </>
              )}
          </Stack>
        )}
      </Card>
    </Box>
  );
}

// ============================================================================
// 1:1 Coaching Section (AC1–AC4, AC13, AC14, AC15)
// ============================================================================

function getStatusBadge(session: ParticipantOneOnOneSession) {
  const { status } = session;
  // MED-1360: a past session the coach has not marked is no longer scheduled.
  if (isPastAwaitingAttendance(session)) {
    return <AttendancePendingBadge />;
  }
  if (status === 'scheduled') {
    return (
      <Badge size="xs" color="medleyGreen" variant="light" style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}>
        Scheduled
      </Badge>
    );
  }
  if (status === 'completed') {
    return (
      <Badge size="xs" color="gray" variant="light" style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}>
        Completed
      </Badge>
    );
  }
  if (status === 'cancelled') {
    return (
      <Badge size="xs" color="dangerRed" variant="light" style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}>
        Canceled
      </Badge>
    );
  }
  return null;
}

// AC24: Check if session is within 24-hour notice period
function isWithin24Hours(scheduledStart: string | null): boolean {
  if (!scheduledStart) return false;
  const diff = new Date(scheduledStart).getTime() - Date.now();
  return diff > 0 && diff < 24 * 60 * 60 * 1000;
}

// Cancel confirmation dialog (AC10)
function CancelConfirmationModal({
  opened,
  onClose,
  onConfirm,
  isLoading,
  sessionLabel,
  scheduledStart,
  error,
}: {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  sessionLabel: string;
  scheduledStart: string | null;
  error: string | null;
}) {
  const showWarning = scheduledStart && isWithin24Hours(scheduledStart);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={700} size="lg">Cancel Session</Text>}
      size="sm"
      radius="lg"
      centered
    >
      <Stack gap="md">
        {showWarning && (
          <Alert
            color="warningAmber"
            icon={<AlertTriangle size={16} />}
            variant="light"
            radius="md"
          >
            <Text size="sm">
              This session is within 24 hours. The coach may have already prepared for it.
            </Text>
          </Alert>
        )}

        <Text size="sm">
          Are you sure you want to cancel <Text span fw={600}>{sessionLabel}</Text>?
        </Text>

        {scheduledStart && (
          <Text size="sm" c="dimmed">
            {formatDate(scheduledStart)} at {formatTime(scheduledStart)}
          </Text>
        )}

        <Text size="xs" c="dimmed">
          Both you and your coach will receive a cancellation email.
        </Text>

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" color="gray" onClick={onClose} disabled={isLoading}>
            Keep Session
          </Button>
          <Button color="dangerRed" loading={isLoading} onClick={onConfirm}>
            Cancel Session
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function OneOnOneSessionCard({
  session,
  totalSessions,
  isNextToSchedule,
  coachCalendlyConnected,
  sectionCoachId,
  onSchedule,
  onReschedule,
  onCancel,
}: {
  session: ParticipantOneOnOneSession;
  totalSessions: number;
  /** MED-1350: the "Your Coach" id. A session run by someone else names its coach. */
  sectionCoachId: string | null;
  isNextToSchedule: boolean;
  /** MED-845: false when the assigned coach has not connected Calendly. Disables the
   *  Schedule CTA on unscheduled rows and shows the "Awaiting Coach Schedule" badge. */
  coachCalendlyConnected: boolean;
  onSchedule: (session: ParticipantOneOnOneSession) => void;
  onReschedule: (session: ParticipantOneOnOneSession) => void;
  onCancel: (session: ParticipantOneOnOneSession) => void;
}) {
  const sessionLabel = session.session_number != null
    ? `Session ${session.session_number}`
    : `Session`;
  const durationLabel = session.duration_minutes ? `${session.duration_minutes} min` : null;
  // AC9/AC10: Show full type label so participants know how to prepare
  const typeConfig = getOneOnOneTypeConfig(session.one_on_one_type);
  const isTwoOnOne = session.one_on_one_type === 'two_on_one';
  // MED-845: Surface "Awaiting Coach Schedule" only on unscheduled rows — the message
  // is meaningless on scheduled/completed/cancelled rows.
  const showAwaitingCoachSchedule =
    session.status === 'unscheduled' && !coachCalendlyConnected;
  // MED-1350: name the session's coach when it is not the "Your Coach" above.
  const otherCoachName =
    session.coach_id && session.coach_name && session.coach_id !== sectionCoachId
      ? session.coach_name
      : null;

  return (
    <Group id={oneOnOneRowId(session.id)} tabIndex={-1} justify="space-between" wrap="nowrap" py="xs">
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap={6} wrap="nowrap">
          <Text size="sm" fw={500}>{sessionLabel} of {totalSessions}</Text>
          {durationLabel && (
            <Badge size="xs" variant="light" color="gray">{durationLabel}</Badge>
          )}
          {/* AC9: Type label on every session (pre-scheduling and scheduled) */}
          <Tooltip label={typeConfig.description} withArrow position="top" multiline w={220}>
            <Badge
              size="xs"
              variant={typeConfig.badgeVariant}
              color={typeConfig.badgeColor}
              style={{ fontWeight: 700, letterSpacing: '0.3px' }}
            >
              {typeConfig.fullLabel}
            </Badge>
          </Tooltip>
          {/* AC9a: "Includes Manager" indicator for two_on_one */}
          {isTwoOnOne && (
            <Badge size="xs" variant="outline" color="surveyPurple" leftSection={<Users size={10} />}>
              Includes Manager
            </Badge>
          )}
        </Group>
        {otherCoachName && (
          <Text size="xs" c="dimmed">With {otherCoachName}</Text>
        )}
        {session.status === 'unscheduled' ? (
          <Text size="xs" c="dimmed">Not yet scheduled</Text>
        ) : session.scheduled_start ? (
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              {formatDate(session.scheduled_start)} · {formatTime(session.scheduled_start)}
            </Text>
            {session.video_link && (
              <Text
                size="xs"
                c="blue"
                component="a"
                href={session.video_link}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                Join Meeting
              </Text>
            )}
          </Stack>
        ) : null}
      </Box>
      <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
        {getStatusBadge(session)}
        {showAwaitingCoachSchedule && (
          <Tooltip
            label={AWAITING_COACH_TOOLTIP_COPY}
            withArrow
            position="top"
            multiline
            w={260}
          >
            <Badge size="sm" variant="light" color="gray">
              {AWAITING_COACH_SCHEDULE_LABEL}
            </Badge>
          </Tooltip>
        )}
        {session.status === 'unscheduled' && (
          <Button
            size="xs"
            color="medleyGreen"
            variant="subtle"
            rightSection={<ArrowRight size={12} />}
            onClick={() => onSchedule(session)}
            disabled={!isNextToSchedule || !coachCalendlyConnected}
            px={4}
          >
            Schedule
          </Button>
        )}
        {/* AC1/AC9: Reschedule/Cancel for scheduled sessions booked online.
            MED-1360: only before the session ends. MED-1365: a booking that
            Medley sent as a Google invite (calendly_fallback) too. */}
        {canChangeBooking(session) &&
          (session.scheduling_method === 'calendly' ||
            session.scheduling_method === 'calendly_fallback') && (
          <Menu shadow="md" width={160} position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="sm">
                <MoreVertical size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<RefreshCw size={14} />}
                onClick={() => onReschedule(session)}
              >
                Reschedule
              </Menu.Item>
              <Menu.Item
                leftSection={<XCircle size={14} />}
                color="red"
                onClick={() => onCancel(session)}
              >
                Cancel
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>
    </Group>
  );
}

function OneOnOneSection({
  coaching,
  cohortName,
  onRefresh,
  checkinRows,
}: {
  coaching: ParticipantOneOnOneCoaching;
  cohortName: string;
  onRefresh: () => void;
  /**
   * MED-1350: the Intro and Midpoint check-in rows. A check-in is a 1:1, so it
   * shows here: Intro first, Midpoint after the first half of the sessions.
   */
  checkinRows?: { intro: ReactNode; midpoint: ReactNode };
}) {
  const [schedulingSession, setSchedulingSession] = useState<ParticipantOneOnOneSession | null>(null);
  const [reschedulingSession, setReschedulingSession] = useState<ParticipantOneOnOneSession | null>(null);
  const [cancelingSession, setCancelingSession] = useState<ParticipantOneOnOneSession | null>(null);
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const handleCancel = async () => {
    if (!cancelingSession) return;
    setIsCanceling(true);
    setCancelError(null);
    try {
      await participantPortalApi.cancelSession(cancelingSession.id);
      setCancelingSession(null);
      onRefresh();
    } catch (err) {
      setCancelError(
        err instanceof Error ? err.message : 'Failed to cancel session. Please try again.',
      );
    } finally {
      setIsCanceling(false);
    }
  };

  const closeCancelModal = () => {
    setCancelingSession(null);
    setCancelError(null);
  };

  const coach = coaching.coach;
  const initials = coach
    ? `${coach.first_name.charAt(0)}${coach.last_name.charAt(0)}`.toUpperCase()
    : '?';

  // MED-709: Only the first unscheduled session in sequence can be scheduled.
  const firstUnscheduledId =
    coaching.sessions.find((s) => s.status === 'unscheduled')?.id ?? null;
  // MED-1350: the Midpoint row follows the first half of the sessions, or ends
  // the list when there are none.
  const midpointAfter =
    coaching.sessions.length > 0 ? Math.ceil(coaching.sessions.length / 2) - 1 : -1;

  return (
    <>
      <Box mb="lg">
        <Text
          size="xs"
          fw={600}
          tt="uppercase"
          c="dimmed"
          mb="sm"
          style={{ letterSpacing: '0.5px' }}
        >
          1:1 Coaching
        </Text>

        {/* Coach card (AC2) */}
        {coach && (
          <Box
            mb="md"
            p="sm"
            style={{ backgroundColor: '#F5F3EF', borderRadius: 8 }}
          >
            <Group justify="space-between" wrap="nowrap">
              <Group gap="sm" wrap="nowrap">
                <Avatar
                  size={44}
                  radius="xl"
                  color="medleyGreen"
                  variant="filled"
                >
                  {initials}
                </Avatar>
                <Box>
                  <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.3px' }}>
                    Your Coach
                  </Text>
                  <Text fw={600} size="sm">{coach.first_name} {coach.last_name}</Text>
                  {coach.job_title && <Text size="xs" c="dimmed">{coach.job_title}</Text>}
                </Box>
              </Group>
              <Box ta="right" style={{ flexShrink: 0 }}>
                <Text fw={700} size="md">
                  {coaching.scheduled_count}/{coaching.session_count}
                </Text>
                {/* MED-1360: the count is booked sessions (scheduled or completed,
                    from the API). A past session waiting for attendance is
                    still booked, so the label says booked, not scheduled. */}
                <Text size="xs" c="dimmed">booked</Text>
              </Box>
            </Group>
          </Box>
        )}

        {/* Session cards (AC3, AC4) */}
        <Card withBorder radius="md" padding="md">
          <Stack gap={0}>
            {/* MED-1350: the check-in rows keep their own full-width style. */}
            {checkinRows?.intro && (
              <Box mx="-md" mt="-md" mb="xs">
                {checkinRows.intro}
              </Box>
            )}
            {coaching.sessions.map((session, i) => (
              <Box key={session.id}>
                {i > 0 && <Divider my="xs" />}
                <OneOnOneSessionCard
                  session={session}
                  totalSessions={coaching.session_count}
                  isNextToSchedule={session.id === firstUnscheduledId}
                  coachCalendlyConnected={coaching.coach?.calendly_connected ?? false}
                  sectionCoachId={coaching.coach?.id ?? null}
                  onSchedule={setSchedulingSession}
                  onReschedule={setReschedulingSession}
                  onCancel={setCancelingSession}
                />
                {checkinRows?.midpoint && i === midpointAfter && (
                  <Box mx="-md" my="xs">
                    {checkinRows.midpoint}
                  </Box>
                )}
              </Box>
            ))}
            {checkinRows?.midpoint && midpointAfter < 0 && (
              <Box mx="-md" mb="-md" mt="xs">
                {checkinRows.midpoint}
              </Box>
            )}
          </Stack>
        </Card>
      </Box>

      {/* Scheduling modal (AC5–AC17) */}
      {schedulingSession && (
        <ScheduleSessionModal
          opened={schedulingSession !== null}
          onClose={() => setSchedulingSession(null)}
          onSuccess={onRefresh}
          session={schedulingSession}
          totalSessions={coaching.session_count}
          coach={coaching.coach}
          cohortName={cohortName}
          defaultMeetingLink={coaching.default_meeting_link}
        />
      )}

      {/* Reschedule modal (E22-4) */}
      {reschedulingSession && (
        <ScheduleSessionModal
          opened={reschedulingSession !== null}
          onClose={() => setReschedulingSession(null)}
          onSuccess={() => {
            setReschedulingSession(null);
            onRefresh();
          }}
          session={reschedulingSession}
          totalSessions={coaching.session_count}
          // Round 4 review: the session's own coach, which the reschedule
          // books with (MED-1350 per-session coaches).
          coach={reschedulingSession.coach ?? coaching.coach}
          cohortName={cohortName}
          defaultMeetingLink={coaching.default_meeting_link}
          rescheduleMode
        />
      )}

      {/* Cancel confirmation dialog (E22-4 AC10) */}
      <CancelConfirmationModal
        opened={cancelingSession !== null}
        onClose={closeCancelModal}
        onConfirm={handleCancel}
        isLoading={isCanceling}
        sessionLabel={
          cancelingSession?.session_number != null
            ? `Session ${cancelingSession.session_number} of ${coaching.session_count}`
            : 'this session'
        }
        scheduledStart={cancelingSession?.scheduled_start ?? null}
        error={cancelError}
      />
    </>
  );
}

// ============================================================================
// Main Dashboard Component
// ============================================================================

export function ParticipantDashboard() {
  const { user } = useAuthStore();
  const { selectedCohortId } = useParticipantCohortStore();
  const firstName = user?.first_name || 'there';
  // MED-938 / E3.5: gate the refreshed two-column dashboard behind the flag.
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');

  const [data, setData] = useState<ParticipantDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await participantPortalApi.getDashboard(selectedCohortId ?? undefined);
      setData(result);
    } catch (error) {
      console.error('Failed to fetch participant dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCohortId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const [actionItemSchedulingSession, setActionItemSchedulingSession] =
    useState<ParticipantOneOnOneSession | null>(null);

  // MED-699: Action Items Intro/Midpoint open the type-aware check-in modal
  const [checkinActionItem, setCheckinActionItem] =
    useState<{ huddleType: 'intro' | 'midpoint_checkin' } | null>(null);

  // MED-1350: one set of check-in dialogs for the Group Coaching and 1:1 sections.
  const checkins = useCheckinActions(
    data?.group_coaching ?? null,
    data?.cohort?.id ?? '',
    data?.cohort?.name ?? '',
    fetchDashboard,
  );

  // Issue 1: Action Items "Schedule" opens modal for first unscheduled 1:1 session
  const handleScheduleFromActionItem = (_coachId: string) => {
    const coaching = data?.one_on_one_coaching;
    if (!coaching) return;
    const firstUnscheduled = coaching.sessions.find(s => s.status === 'unscheduled');
    if (firstUnscheduled) {
      setActionItemSchedulingSession(firstUnscheduled);
    }
  };

  // MED-699: Action Items Intro/Midpoint → open check-in modal at dashboard level
  const handleScheduleCheckinFromActionItem = (
    _coachId: string,
    huddleType: 'intro' | 'midpoint_checkin',
  ) => {
    setCheckinActionItem({ huddleType });
  };

  if (isLoading) {
    return (
      <Center py="xl" style={{ minHeight: 'calc(100vh - 60px)' }}>
        <Loader color="medleyGreen" />
      </Center>
    );
  }

  const hasAnnounced = (data?.announced_modalities?.length ?? 0) > 0;
  const liveSession = data?.live_session ?? null;
  // MED-1181: the per-cohort half of the LSD gate. Every LSD affordance on the
  // dashboard (refresh layout, live/starting-soon/peer-leader banners, the
  // "Reflect" pre-session link) must AND the global flag together with the
  // cohort's group-coaching modality setting. Fails closed for classic MED01
  // cohorts (live_session_delivery_enabled = false), so none of it renders.
  const cohortLiveSessionDeliveryEnabled =
    liveSessionDeliveryEnabled && (data?.live_session_delivery_enabled ?? false);

  // Page-owned scheduling modals — shared by both the legacy and refreshed
  // dashboards so 1:1/check-in scheduling behaves identically in either view.
  const schedulingModals = (
    <>
      {actionItemSchedulingSession && data?.one_on_one_coaching && (
        <ScheduleSessionModal
          opened={actionItemSchedulingSession !== null}
          onClose={() => setActionItemSchedulingSession(null)}
          onSuccess={fetchDashboard}
          session={actionItemSchedulingSession}
          totalSessions={data.one_on_one_coaching.session_count}
          coach={data.one_on_one_coaching.coach}
          cohortName={data.cohort?.name ?? ''}
          defaultMeetingLink={data.one_on_one_coaching.default_meeting_link}
        />
      )}
      {checkinActionItem && data?.group_coaching?.coach && data?.cohort && (
        <ScheduleCheckinModal
          opened={checkinActionItem !== null}
          onClose={() => setCheckinActionItem(null)}
          onSuccess={() => {
            setCheckinActionItem(null);
            fetchDashboard();
          }}
          coach={data.group_coaching.coach}
          cohortId={data.cohort.id}
          cohortName={data.cohort.name}
          initialHuddleType={checkinActionItem.huddleType}
        />
      )}
    </>
  );

  // MED-938 / E3.5: when the flag is on and the API returned the refresh block,
  // render the new two-column dashboard. Otherwise fall through to the legacy
  // dashboard unchanged (no-regression ACs).
  if (cohortLiveSessionDeliveryEnabled && data?.refresh) {
    return (
      <>
        <DashboardRefreshView
          data={data}
          firstName={firstName}
          onScheduleCoaching={handleScheduleFromActionItem}
          onScheduleCheckin={handleScheduleCheckinFromActionItem}
        />
        {schedulingModals}
      </>
    );
  }

  return (
    <Container size="md" py="xl" className="med-mobile-container">
      {/* AC6: Welcome Banner */}
      <WelcomeBanner
        firstName={firstName}
        cohortName={data?.cohort?.name ?? 'your'}
      />

      {/* MED-950: Live Session Banner — shown whenever the participant has a
          session currently in_progress. MED-1181: this is an LSD affordance, so
          it is gated on the cohort's LSD modality (flag + modality) and never
          renders for a classic MED01 cohort. */}
      {cohortLiveSessionDeliveryEnabled && liveSession && (
        <LiveSessionBanner session={liveSession} />
      )}

      {/* AC7, AC16: Starting Soon Banner — only when session within 30 min.
          MED-1181: LSD affordance — gate on cohort LSD modality. */}
      {cohortLiveSessionDeliveryEnabled && data?.starting_soon && (
        <StartingSoonBanner session={data.starting_soon} />
      )}

      {/* MED-813 / E4.1 AC #1: Peer-leader callout when the participant is the
          designated leader of an upcoming peer-led session. MED-1181: LSD
          affordance — gate on cohort LSD modality. */}
      {cohortLiveSessionDeliveryEnabled && data?.peer_leader_callout && (
        <PeerLeaderCallout callout={data.peer_leader_callout} />
      )}

      {/* AC14 + MED-950: Empty state ONLY when no modalities announced AND no
          live session. A live session implies the program is NOT "being set
          up" — the banner above is enough; suppress the misleading empty
          state and skip the announced-modality sections (which would render
          empty Upcoming Sessions and My Program cards with no useful data). */}
      {!hasAnnounced && !liveSession && <NoModalitiesState />}
      {hasAnnounced && (
        <>
          {/* AC8, AC17 + MED-699 AC10/AC11/AC13: Action Items */}
          <ActionItemsSection
            items={data?.action_items ?? []}
            onScheduleCoaching={handleScheduleFromActionItem}
            onScheduleCheckin={handleScheduleCheckinFromActionItem}
          />

          {/* AC9, AC10: Upcoming Sessions */}
          <UpcomingSessionsSection
            sessions={data?.upcoming_sessions ?? []}
            liveSessionDeliveryEnabled={cohortLiveSessionDeliveryEnabled}
          />

          {/* E22-3: Group Coaching Section */}
          {data?.group_coaching && (
            <GroupCoachingSection
              coaching={data.group_coaching}
              checkins={checkins}
              // MED-1350: a check-in is a 1:1. It shows in the 1:1 section when
              // the cohort has one.
              showTypedCheckins={!data.one_on_one_coaching}
            />
          )}

          {/* AC1–AC4: 1:1 Coaching Section */}
          {data?.one_on_one_coaching && (
            <OneOnOneSection
              coaching={data.one_on_one_coaching}
              cohortName={data.cohort?.name ?? ''}
              onRefresh={fetchDashboard}
              checkinRows={{
                intro: checkins.renderCheckinRow('intro'),
                midpoint: checkins.renderCheckinRow('midpoint_checkin'),
              }}
            />
          )}

          {/* MED-569: Workshops Section */}
          {data?.workshops && data.workshops.length > 0 && (
            <WorkshopsSection workshops={data.workshops} />
          )}

          {/* AC11, AC12: My Program */}
          {data?.cohort && (
            <MyProgramSection
              cohortName={data.cohort.name}
              partnerName={data.cohort.partner_name}
              startDate={data.cohort.start_date}
              endDate={data.cohort.end_date}
              modalities={data.announced_modalities}
            />
          )}
        </>
      )}
      {schedulingModals}
      {checkins.modals}
    </Container>
  );
}

export default ParticipantDashboard;
